import React, { useEffect, useState } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import UserTable from "../components/UserTable";
import UserForm from "../components/UserForm";
import LinkModal from "../components/LinkModal";
import {
  fetchUsers,
  createUser,
  deleteUser,
  updateUserEndDate,
  updatePassword,
} from "../api/userRequests";
import {
  fetchLink,
  createLink,
  deleteLink,
  updateLink,
} from "../api/linkRequests";
import UpdateEndDateModal from "../components/UpdateEndDateModal";

interface User {
  email: string;
  endDate: string;
}

const AdminUsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [accountsQuanty, setAccountsQuanty] = useState<number>(0);
  const [openForm, setOpenForm] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [currentLink, setCurrentLink] = useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [currentEndDate, setCurrentEndDate] = useState<string | null>(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data.users);
      setAccountsQuanty(data.accountsQuanty);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const loadLink = async () => {
    try {
      const link = await fetchLink();
      setCurrentLink(link); // Устанавливаем либо ссылку, либо null
    } catch (error) {
      console.error("Failed to load link:", error);
      setCurrentLink(null); // Обработка ошибки
    }
  };

  const handleAddUsers = async (
    users: { email: string; endDate: string }[]
  ) => {
    setLoading(true);
    try {
      const result = await createUser(users);
      console.log(result);
      loadUsers();
    } finally {
      setLoading(false);
      setOpenForm(false);
    }
  };

  const handleDeleteUser = async (email: string) => {       
    await deleteUser(email);
    loadUsers();
  };

  const handleCreateLink = async (link: string) => {
    try {
      await createLink(link);
      loadLink(); // Обновляем ссылку после создания
      setOpenLinkModal(false);
    } catch (error) {
      console.error("Failed to create link:", error);
    }
  };

  const handleEditLink = async (link: string) => {
    try {
      await updateLink(link); // Вызываем обновление ссылки
      loadLink(); // Обновляем отображаемую ссылку
      setOpenLinkModal(false);
    } catch (error) {
      console.error("Failed to update link:", error);
    }
  };

  const handleEditEndDate = (email: string, endDate: string) => {
    setCurrentEmail(email);
    setCurrentEndDate(endDate);
    setOpenUpdateModal(true);
  };

  const handleUpdateEndDate = async (endDate: string) => {
    if (currentEmail) {
      await updateUserEndDate(currentEmail, endDate);
      loadUsers();
      setOpenUpdateModal(false);
    }
  };

  const handleUpdatePassword = async (email: string, password: string) => {
      const result = await updatePassword(email, password);
      setResponseMessage(result);
  };

 

  // const handleDeleteLink = async () => {
  //   try {
  //     await deleteLink(); // Удаляем ссылку
  //     setCurrentLink(null); // Обновляем состояние
  //   } catch (error) {
  //     console.error("Failed to delete link:", error);
  //   }
  // };

  useEffect(() => {
    loadUsers();
    loadLink();
  }, []);

  return (
    <Container>
      <Box marginTop={4}>
        <Typography variant="h5" marginBottom={2}>
          Total Accounts: {accountsQuanty}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenForm(true)}
        >
          Add Users
        </Button>
        {currentLink ? (
          <Box display="flex" alignItems="center" sx={{ marginLeft: 2 }}>
            <Typography variant="body1" sx={{ marginRight: 2 }}>
              Journal Link:{" "}
              <a href={currentLink} target="_blank" rel="noopener noreferrer">
                {currentLink}
              </a>
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenLinkModal(true)} // Открываем модал для изменения ссылки
              sx={{ marginRight: 1 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                await deleteLink();
                setCurrentLink(null); // Сбрасываем ссылку после удаления
              }}
            >
              Delete
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setOpenLinkModal(true)}
            sx={{ marginLeft: 2 }}
          >
            Add Link to Journal
          </Button>             
        )}
        <UserTable
          responseMessage={responseMessage || ""}
          users={users}
          onDelete={handleDeleteUser}
          onEditEndDate={handleEditEndDate}
          onUpdatePassword={handleUpdatePassword}
        />
        <UserForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          onSubmit={handleAddUsers}
          loading={loading}
          accountsQuanty={accountsQuanty - users.length}
        />
        <LinkModal
          open={openLinkModal}
          onClose={() => setOpenLinkModal(false)}
          onSubmit={currentLink ? handleEditLink : handleCreateLink}
          defaultLink={currentLink || undefined}
        />
        <UpdateEndDateModal
          open={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
          onSubmit={handleUpdateEndDate}
          currentEndDate={currentEndDate || ""}
        />
      </Box>
    </Container>
  );
};

export default AdminUsersPage;
